import React, { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { ReasoningMessage as ReasoningMessageType, ReasoningStep } from 'types/chat.types';
import { useEvidencePanel, EvidenceItem } from './EvidencePanelContext';

/* ── Constants ────────────────────────────────────────────────────────────── */

const PHASE_ORDER = [
  'chapter_identification',
  'heading_selection',
  'subheading_drill_down',
  'validation_and_finalization',
] as const;

const PHASE_LABELS: Record<string, string> = {
  chapter_identification: 'Chapter Identification',
  heading_selection: 'Heading Selection',
  subheading_drill_down: 'Subheading Drill-Down',
  validation_and_finalization: 'Validation & Finalization',
  tariff_determination: 'Calculating Tariffs',
};

// Action phrases for the in-progress (spinning) tool indicator.
const TOOL_LABELS: Record<string, string> = {
  hts_lookup: 'Looking up HTS database',
  hts_notes: 'Consulting HTS Notes',
  cross_search: 'Searching CROSS rulings',
  web_search: 'Searching external sources',
  get_document_context: 'Reading document',
  tariff_impact: 'Determining tariff impact',
  ask_user: 'Requesting clarification',
};

// Noun-form names for completed-tool summaries, e.g. "0 results from HTS Notes"
// or "Tariff impact completed". Kept separate from the action phrases above so
// both contexts read naturally.
const TOOL_NOUNS: Record<string, string> = {
  hts_lookup: 'HTS database',
  hts_notes: 'HTS Notes',
  cross_search: 'CROSS rulings',
  web_search: 'external sources',
  get_document_context: 'document context',
  tariff_impact: 'Tariff impact',
  ask_user: 'clarification',
};

/**
 * Human-readable name for a tool in a completed-step summary. Falls back to
 * title-casing the snake_case identifier (e.g. "some_tool" -> "Some tool") so
 * no raw identifiers ever leak into the UI.
 */
function toolNoun(toolName: string): string {
  if (TOOL_NOUNS[toolName]) return TOOL_NOUNS[toolName];
  const spaced = toolName.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/* ── Streaming prose ──────────────────────────────────────────────────────── */

const PROSE_CLASSES = `prose prose-sm prose-gray max-w-none
  prose-p:text-[14px] prose-p:text-gray-700 prose-p:leading-[1.75] prose-p:my-2
  prose-strong:text-gray-900 prose-strong:font-semibold
  prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-li:text-[14px] prose-li:text-gray-700
  prose-headings:text-gray-900 prose-headings:font-semibold
  prose-h2:text-base prose-h3:text-sm prose-h4:text-sm
  prose-code:text-xs prose-code:text-teal-700 prose-code:bg-gray-50 prose-code:px-1 prose-code:rounded
  prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline
  prose-table:text-xs prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2
  prose-table:border prose-th:border prose-td:border prose-th:bg-gray-50`;

// `split` runs the per-sentence reveal animation. Disable it for text that
// contains dotted tokens (e.g. an HTS code like 6403.91.90), which the sentence
// splitter would otherwise shatter across lines.
const StreamingSentences: React.FC<{ text: string; animate?: boolean; split?: boolean }> = ({
  text,
  animate = true,
  split = true,
}) => {
  if (!split) {
    return (
      <div className={PROSE_CLASSES}>
        <Markdown>{text}</Markdown>
      </div>
    );
  }

  const sentences = splitIntoSentences(text);

  return (
    <div className={PROSE_CLASSES}>
      {sentences.map((sentence, i) => (
        <span
          key={i}
          className={animate ? 'animate-fade-slide-in inline' : 'inline'}
          style={animate ? { animationDelay: `${i * 40}ms`, animationFillMode: 'both' } : undefined}
        >
          <Markdown>{sentence}</Markdown>
        </span>
      ))}
    </div>
  );
};

function splitIntoSentences(text: string): string[] {
  if (!text) return [];
  // Split on sentence boundaries but keep markdown structure intact.
  // Use paragraph breaks as primary splits, then sentences within paragraphs.
  const paragraphs = text.split(/\n\n+/);
  if (paragraphs.length > 1) return paragraphs;
  // Single paragraph — break ONLY on whitespace that follows sentence-ending
  // punctuation and precedes a likely new-sentence start (capital letter, quote,
  // bracket, or markdown emphasis). This keeps dotted tokens intact — an HTS code
  // like 6403.51.90 or a decimal has no space after its periods — and avoids
  // shattering a **bold** span (which would leave stray literal asterisks).
  // Uses a sentinel + lookahead (no lookbehind) for broad browser support.
  const SENTINEL = '\u0000';
  const parts = text
    .replace(/([.!?])\s+(?=["'(*[A-Z-])/g, `$1${SENTINEL}`)
    .split(SENTINEL);
  return parts.length ? parts : [text];
}

/* ── Tariff determination steps (deterministic-path streaming) ─────────────── */
/* The deterministic path has no LLM thinking to stream, so it streams structured
   lookup / calculation / flag steps as what they are — fast, factual, with
   provenance. No fake "thinking" is manufactured. */

const TariffStep: React.FC<{ step: ReasoningStep }> = ({ step }) => {
  const d = step.data || {};

  // Plain-language narration of the methodology — what's happening behind the
  // scenes while duties are calculated.
  if (step.type === 'tariff_narration') {
    return (
      <div className="animate-fade-slide-in">
        <StreamingSentences text={d.content || ''} animate split={false} />
      </div>
    );
  }

  if (step.type === 'tariff_lookup_step') {
    const applicable = d.applicable;
    const dot = applicable === true ? 'bg-teal-500' : applicable === false ? 'bg-gray-300' : 'bg-amber-400';
    return (
      <div className="flex items-start gap-2 py-0.5 animate-fade-slide-in">
        <span className={`w-1.5 h-1.5 rounded-full ${dot} mt-1.5 flex-shrink-0`} />
        <div className="min-w-0">
          <span className="text-[13px] text-gray-700">
            <span className="font-medium text-gray-900">{d.program}</span>
            {d.result ? <span className="text-gray-600">: {d.result}</span> : null}
            {d.as_of ? <span className="text-[11px] text-gray-400"> (as of {d.as_of})</span> : null}
          </span>
          {d.note ? <p className="text-[11px] text-gray-400 leading-snug mt-0.5">{d.note}</p> : null}
        </div>
      </div>
    );
  }

  if (step.type === 'tariff_calculation_step') {
    const order = Array.isArray(d.stacking_order) ? d.stacking_order.join(' + ') : '';
    return (
      <div className="flex items-start gap-2 py-0.5 animate-fade-slide-in">
        <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
        <div className="min-w-0">
          <span className="text-[13px] text-gray-700">
            Stacking <span className="text-gray-500">{order}</span> →{' '}
            <span className="font-semibold text-gray-900">{d.total_duty_pct}%</span>
            <span className="text-[11px] text-gray-400"> on customs value</span>
          </span>
          {d.note ? <p className="text-[11px] text-gray-400 leading-snug mt-0.5">{d.note}</p> : null}
        </div>
      </div>
    );
  }

  if (step.type === 'tariff_flag_raised') {
    const sev = d.severity || 'warning';
    const cls =
      sev === 'critical'
        ? 'bg-red-50 border-red-200 text-red-800'
        : sev === 'info'
        ? 'bg-gray-50 border-gray-200 text-gray-700'
        : 'bg-amber-50 border-amber-200 text-amber-800';
    return (
      <div className={`flex items-start gap-2 py-1.5 px-2.5 rounded-md border text-[12px] animate-fade-slide-in ${cls}`}>
        <span className="font-semibold uppercase tracking-wide text-[10px] mt-0.5 flex-shrink-0">
          {d.kind === 'addcvd' ? 'ADD/CVD' : d.kind === 'trq' ? 'TRQ' : d.kind === 'value_required' ? 'Value' : 'Flag'}
        </span>
        <span>{d.message}</span>
      </div>
    );
  }

  if (step.type === 'tariff_determination_complete') {
    return (
      <div className="flex items-center gap-2 py-1 animate-fade-slide-in">
        <span className="w-3 h-3 rounded-full bg-teal-500 flex-shrink-0" />
        <span className="text-[13px] font-medium text-gray-900">
          Tariff determination: {d.rate_summary || `${d.total_duty_pct}%`}
        </span>
      </div>
    );
  }

  return null;
};

/* ── Tool call indicator (only shown while loading — hidden once result arrives) */

const ToolCallIndicator: React.FC<{ step: ReasoningStep }> = ({ step }) => {
  const toolName = step.data.name || 'unknown';
  const label = TOOL_LABELS[toolName] || `Running ${toolName}`;
  const input = step.data.input || {};
  const query = input.description || input.heading || input.chapter || input.product_description || '';

  return (
    <div className="flex items-center gap-2 py-1">
      <div className="w-3 h-3 border-[1.5px] border-teal-400 border-t-transparent rounded-full animate-spin flex-shrink-0 opacity-60" />
      <span className="text-xs text-gray-400">
        {label}
        {query && <span className="ml-1">— {String(query).slice(0, 35)}{String(query).length > 35 ? '...' : ''}</span>}
      </span>
    </div>
  );
};

/* ── Tool result summary ──────────────────────────────────────────────────── */

const ToolResultSummary: React.FC<{
  step: ReasoningStep;
  onViewDetails: () => void;
}> = ({ step, onViewDetails }) => {
  const toolName = step.data.tool || 'tool';
  const toolLabel = toolNoun(toolName);
  const data = step.data.result_data;
  const preview = step.data.result_preview || '';

  let summary = '';
  if (data?.results?.length !== undefined) {
    summary = `${data.results.length} result${data.results.length !== 1 ? 's' : ''} from ${toolLabel}`;
  } else if (preview) {
    summary = preview.slice(0, 60) + (preview.length > 60 ? '...' : '');
  } else {
    summary = `${toolLabel} completed`;
  }

  return (
    <button
      onClick={onViewDetails}
      className="flex items-center gap-2.5 py-2 px-3 w-full text-left rounded-lg border border-gray-150 bg-gray-50/50 hover:bg-teal-50 hover:border-teal-200 transition-all group cursor-pointer"
    >
      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-xs text-gray-700 flex-1 truncate font-medium">
        {summary}
      </span>
      <span className="text-[10px] text-teal-600 font-medium opacity-70 group-hover:opacity-100 transition-opacity whitespace-nowrap">
        View details
      </span>
    </button>
  );
};

/* ── Phase section ────────────────────────────────────────────────────────── */

interface PhaseGroup {
  phase: string;
  steps: ReasoningStep[];
}

const groupByPhase = (steps: ReasoningStep[]): PhaseGroup[] => {
  if (!Array.isArray(steps)) return [];
  const groups: PhaseGroup[] = [];
  let current: PhaseGroup | null = null;

  for (const step of steps) {
    if (step.type === 'phase_change') continue;
    const phase = step.phase || 'chapter_identification';
    if (!current || current.phase !== phase) {
      current = { phase, steps: [] };
      groups.push(current);
    }
    current.steps.push(step);
  }
  return groups;
};

type PhaseStatus = 'completed' | 'active' | 'upcoming';

const getPhaseStatus = (phase: string, activePhase: string, isComplete: boolean): PhaseStatus => {
  if (isComplete) return 'completed';
  // Tariff calculation (and any non-classification phase) isn't part of the
  // ordered classification flow — it runs inline. Treat it as the active
  // frontier while streaming so it gets the live (teal) styling.
  if (!PHASE_ORDER.includes(phase as any)) return 'active';
  const phaseIdx = PHASE_ORDER.indexOf(phase as any);
  const activeIdx = PHASE_ORDER.indexOf(activePhase as any);
  // When the active phase is inline (e.g. tariff calc, not in PHASE_ORDER), the
  // ordered classification phases that already ran stay completed — don't dim them.
  if (activeIdx === -1) return 'completed';
  if (phaseIdx < activeIdx) return 'completed';
  if (phaseIdx === activeIdx) return 'active';
  return 'upcoming';
};

const PhaseSection: React.FC<{
  group: PhaseGroup;
  status: PhaseStatus;
  isLast: boolean;
  timelineComplete: boolean;
}> = ({ group, status, isLast, timelineComplete }) => {
  const { openPanel } = useEvidencePanel();
  const label = PHASE_LABELS[group.phase] || group.phase.replace(/_/g, ' ');

  // Everything stays open during streaming; collapse when classification finishes
  const [isOpen, setIsOpen] = useState(true);
  const wasTimelineCompleteRef = useRef(timelineComplete);
  useEffect(() => {
    if (timelineComplete && !wasTimelineCompleteRef.current) {
      setIsOpen(false);
    }
    wasTimelineCompleteRef.current = timelineComplete;
  }, [timelineComplete]);

  const handleViewResult = (step: ReasoningStep) => {
    const data = step.data.result_data || tryParseJson(step.data.result_preview);
    if (!data) return;

    const item: EvidenceItem = {
      id: `${step.iteration}-${step.data.tool}-${Date.now()}`,
      tool: step.data.tool || 'tool',
      label: step.data.tool || 'Result',
      data,
      timestamp: new Date(),
    };
    openPanel(item);
  };

  const dotColor = status === 'completed' ? 'bg-green-500' : status === 'active' ? 'bg-teal-500' : 'bg-gray-300';
  const lineColor = status === 'completed' ? 'bg-green-200' : status === 'active' ? 'bg-teal-200' : 'bg-gray-200';
  const textOpacity = status === 'upcoming' ? 'opacity-40' : '';

  const stepSummary = getPhaseStepSummary(group.steps);

  return (
    <div className={`relative flex gap-4 ${textOpacity}`}>
      {/* Timeline rail */}
      <div className="flex flex-col items-center flex-shrink-0 w-5">
        <div className={`w-3 h-3 rounded-full ${dotColor} flex-shrink-0 mt-1 ring-2 ring-white`} />
        {!isLast && <div className={`w-0.5 flex-1 ${lineColor} mt-1`} />}
      </div>

      {/* Content */}
      <div className={`flex-1 min-w-0 ${isLast && !isOpen ? 'pb-2' : 'pb-8'}`}>
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-2 w-full text-left group"
        >
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider group-hover:text-gray-700 transition-colors">
            {label}
          </h3>
          {status === 'completed' && !isOpen && stepSummary && (
            <span className="text-[10px] text-gray-400 truncate flex-1">
              {stepSummary}
            </span>
          )}
          <svg
            className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="space-y-2 mt-3">
            {group.steps.map((step, idx) => {
              if (step.type === 'thinking') {
                return (
                  <div key={idx} className="animate-fade-slide-in">
                    <StreamingSentences
                      text={step.data.content || ''}
                      animate={status === 'active'}
                    />
                  </div>
                );
              }
              if (step.type === 'text') {
                return (
                  <div key={idx} className="animate-fade-slide-in">
                    <StreamingSentences text={step.data.content || ''} animate={status === 'active'} />
                  </div>
                );
              }
              if (step.type === 'tool_call') {
                const hasFollowingResult = group.steps.slice(idx + 1).some(
                  (s) => s.type === 'tool_result'
                );
                if (hasFollowingResult || status === 'completed') return null;
                return (
                  <div key={idx} className="animate-fade-slide-in">
                    <ToolCallIndicator step={step} />
                  </div>
                );
              }
              if (step.type === 'tool_result') {
                return (
                  <div key={idx} className="animate-fade-slide-in">
                    <ToolResultSummary
                      step={step}
                      onViewDetails={() => handleViewResult(step)}
                    />
                  </div>
                );
              }
              if (
                step.type === 'tariff_narration' ||
                step.type === 'tariff_lookup_step' ||
                step.type === 'tariff_calculation_step' ||
                step.type === 'tariff_flag_raised' ||
                step.type === 'tariff_determination_complete'
              ) {
                return <TariffStep key={idx} step={step} />;
              }
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/** Generates a short inline summary of what happened in a collapsed phase. */
function getPhaseStepSummary(steps: ReasoningStep[]): string {
  const toolResults = steps.filter((s) => s.type === 'tool_result');
  const thinkingSteps = steps.filter((s) => s.type === 'thinking' || s.type === 'text');

  const tariffSteps = steps.filter(
    (s) => s.type === 'tariff_lookup_step' || s.type === 'tariff_determination_complete'
  );

  const parts: string[] = [];
  if (toolResults.length > 0) {
    parts.push(`${toolResults.length} lookup${toolResults.length !== 1 ? 's' : ''}`);
  }
  if (tariffSteps.length > 0) {
    parts.push('tariff determined');
  }
  if (thinkingSteps.length > 0) {
    const wordCount = thinkingSteps.reduce((sum, s) => sum + (s.data.content?.split(/\s+/).length || 0), 0);
    if (wordCount > 20) {
      parts.push(`${wordCount} words of analysis`);
    }
  }
  return parts.length > 0 ? `— ${parts.join(', ')}` : '';
}

/* ── Main component ───────────────────────────────────────────────────────── */

interface ReasoningTimelineProps {
  message: ReasoningMessageType;
}

const ReasoningTimeline: React.FC<ReasoningTimelineProps> = ({ message }) => {
  const { steps = [], currentPhase, timestamp, isComplete } = message;
  const bottomRef = useRef<HTMLDivElement>(null);
  const allGroups = groupByPhase(steps);
  // The agent runs tariff_impact multiple times while drilling (one per
  // candidate), each re-streaming the full "Calculating Tariffs" block. Show
  // only the final determination's block to avoid duplicate sections.
  const lastTariffIdx = allGroups.reduce(
    (acc, g, i) => (g.phase === 'tariff_determination' ? i : acc),
    -1
  );
  const groups = allGroups.filter(
    (g, i) => g.phase !== 'tariff_determination' || i === lastTariffIdx
  );
  const [isExpanded, setIsExpanded] = useState(true);

  // Auto-collapse when classification finishes
  const wasCompleteRef = useRef(isComplete);
  useEffect(() => {
    if (isComplete && !wasCompleteRef.current) {
      setIsExpanded(false);
    }
    wasCompleteRef.current = isComplete;
  }, [isComplete]);

  // Auto-open evidence panel for the latest tool result
  const { openPanel } = useEvidencePanel();
  const lastResultRef = useRef<string>('');

  useEffect(() => {
    const toolResults = steps.filter((s) => s.type === 'tool_result');
    const latest = toolResults[toolResults.length - 1];
    if (!latest) return;

    const resultId = `${latest.iteration}-${latest.data.tool}`;
    if (resultId === lastResultRef.current) return;
    lastResultRef.current = resultId;

    const data = latest.data.result_data || tryParseJson(latest.data.result_preview);
    if (!data) return;

    const item: EvidenceItem = {
      id: resultId,
      tool: latest.data.tool || 'tool',
      label: latest.data.tool || 'Result',
      data,
      timestamp: new Date(),
    };
    openPanel(item);
  }, [steps, openPanel]);

  // No steps available (older conversation without saved reasoning)
  if (steps.length === 0) {
    return (
      <div className="self-start flex flex-col w-full max-w-2xl">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-gray-50 border-gray-200">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-400">
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <p className="text-sm text-gray-500">Reasoning steps were not recorded for this classification.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="self-start flex flex-col w-full max-w-2xl">
      {/* Header — clickable when complete */}
      <button
        type="button"
        onClick={() => isComplete && setIsExpanded((v) => !v)}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors w-full text-left ${
          isComplete
            ? 'bg-green-50/60 border-green-200 cursor-pointer hover:bg-green-50'
            : 'bg-gradient-to-r from-teal-50/80 to-white border-teal-200/60 cursor-default'
        } ${isExpanded ? 'mb-6' : ''}`}
      >
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-green-500' : 'bg-teal-600'}`}>
          {isComplete ? (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${isComplete ? 'text-green-800' : 'text-gray-900'}`}>
            {isComplete ? 'Classification Complete' : 'Classifying Product'}
          </p>
          <p className="text-[11px] text-gray-500">
            {isComplete
              ? `Analyzed across ${groups.length} phase${groups.length !== 1 ? 's' : ''}`
              : PHASE_LABELS[currentPhase] || 'Analyzing...'}
          </p>
        </div>
        {!isComplete && (
          <div className="w-4 h-4 border-[1.5px] border-teal-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
        )}
        {isComplete && (
          <svg
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Timeline — collapsible */}
      {isExpanded && (
        <>
          <div className="pl-1">
            {groups.map((group, idx) => (
              <PhaseSection
                key={group.phase + idx}
                group={group}
                status={getPhaseStatus(group.phase, currentPhase, !!isComplete)}
                isLast={idx === groups.length - 1}
                timelineComplete={!!isComplete}
              />
            ))}
          </div>

          {/* Timestamp */}
          <div className="text-[10px] text-gray-400 ml-9 mt-2" ref={bottomRef}>
            {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </>
      )}
    </div>
  );
};

/* ── Utility ──────────────────────────────────────────────────────────────── */

function tryParseJson(text: string | undefined): any {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export default ReasoningTimeline;
